import React, { useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { Box } from 'src/kit/Box';
import { Flex } from 'src/kit/Flex';
import { useJobFeed } from '@src/features/feed/hooks/useJobFeed';
import { getJobs } from '@src/services/jobs';

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.8rem, 5vw, 4.2rem);
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: 24px;
  letter-spacing: -0.02em;

  em {
    font-style: normal;
    color: #2dbd96;
    position: relative;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const HeroSub = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #8a99a8;
  margin-bottom: 40px;
  max-width: 540px;
`;

const StatNumber = styled.div`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;

  span {
    color: #2dbd96;
  }
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #8a99a8;
  margin-top: 4px;
  line-height: 1.4;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2dbd96;
  color: #0d1b2a;
  padding: 14px 32px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    background: #1a9b78;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #ffffff;
  padding: 14px 32px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.95rem;
  text-decoration: none;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #2dbd96;
    color: #2dbd96;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MockJobCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(13, 27, 42, 0.4);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.03);
  margin-bottom: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(45, 189, 150, 0.05);
    border-color: rgba(45, 189, 150, 0.2);
    transform: translateX(4px);
  }
`;

const CompanyLogo = styled.div<{ bg?: string }>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${({ bg }) => bg || 'rgba(255, 255, 255, 0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #ffffff;
  font-size: 1rem;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const JobTag = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: #8a99a8;
  margin-right: 6px;
`;

const EmployerHero = observer(() => {
  const { jobsList, isJobsFetched } = useJobFeed(getJobs, 'jobsList');
  const displayJobs = jobsList.slice(0, 4);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const handleSmoothScroll = (e: MouseEvent) => {
      const target = e.currentTarget as HTMLAnchorElement;
      const hash = target.getAttribute('href');
      if (hash?.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', handleSmoothScroll);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.removeEventListener('click', handleSmoothScroll);
      });
    };
  }, []);

  return (
    <Box
      as="section"
      style={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        background: '#0D1B2A',
        display: 'flex',
        alignItems: 'center',
        padding: '100px 5% 80px',
      }}
    >
      {/* Grid Pattern */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 700,
          height: 700,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45,189,150,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(2,119,189,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
          transform: 'translateX(-50%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -100,
          left: '10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Flex
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box style={{ maxWidth: '680px' }}>
          <Flex style={{ alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
            <Box style={{ width: '32px', height: '2px', background: '#2dbd96' }} />
            <Box
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#2dbd96',
              }}
            >
              Junior Recruitment · Corporate Finance
            </Box>
          </Flex>

          <HeroTitle>
            From posting
            <br />
            to <em>qualified shortlist</em>
            <br />
            in&nbsp;10&nbsp;days.
          </HeroTitle>

          <HeroSub>
            Sainoo is the end-to-end recruitment platform built exclusively for PE funds, investment banks and M&A
            boutiques — covering the full workflow from multi-university distribution to ranked shortlist.
          </HeroSub>

          <Flex style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '56px' }}>
            <PrimaryButton href="#sign-up">Book a demo</PrimaryButton>
            <SecondaryButton href="#workflow">See how it works</SecondaryButton>
          </Flex>

          <Flex
            style={{ display: 'flex', gap: '40px', paddingTop: '40px', borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Box>
              <StatNumber>
                60<span>+</span>
              </StatNumber>
              <StatLabel>Partner Universities</StatLabel>
            </Box>

            <Box>
              <StatNumber>
                300<span>+</span>
              </StatNumber>
              <StatLabel>Corporate Finance Assessment Questions</StatLabel>
            </Box>

            <Box>
              <StatNumber>10</StatNumber>
              <StatLabel>Days to Ranked Shortlist</StatLabel>
            </Box>
          </Flex>
        </Box>

        <Box
          style={{
            background: 'rgba(30, 58, 95, 0.6)',
            border: '1px solid rgba(45, 189, 150, 0.2)',
            borderRadius: '12px',
            padding: '24px',
            backdropFilter: 'blur(8px)',
            width: '420px',
          }}
          css={`
            @media (max-width: 1100px) {
              display: none;
            }
          `}
        >
          <Flex style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <Box style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#2dbd96' }} />
            <Box
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#2dbd96',
              }}
            >
              Recent job postings
            </Box>
          </Flex>

          <Box>
            {!isJobsFetched && (
              <Box style={{ color: '#8a99a8', fontSize: '0.8rem', textAlign: 'center', padding: '40px 20px' }}>
                Loading latest opportunities...
              </Box>
            )}

            {isJobsFetched && displayJobs.length === 0 && (
              <Box style={{ color: '#8a99a8', fontSize: '0.8rem', textAlign: 'center', padding: '40px 20px' }}>
                No active jobs at the moment.
              </Box>
            )}

            {isJobsFetched &&
              displayJobs.map((job, index) => (
                <Link to={`/jobs?scope=all`} key={job.id || job.ability_token} style={{ textDecoration: 'none' }}>
                  <MockJobCard style={{ opacity: index === 3 ? 0.7 : 1 }}>
                    <CompanyLogo bg={job.company?.color}>
                      {job.company?.logo_url || job.company?.logo ? (
                        <img
                          src={job.company?.logo_url || job.company?.logo}
                          alt={job.company?.name}
                          loading="lazy"
                          decoding="async"
                          style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'contain' }}
                        />
                      ) : (
                        job.company?.name?.slice(0, 2).toUpperCase()
                      )}
                    </CompanyLogo>
                    <Box style={{ flex: 1 }}>
                      <Flex style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                        <Box style={{ fontSize: '0.9rem', color: '#ffffff', fontWeight: 600 }}>{job.company?.name}</Box>
                        {index === 0 && (
                          <Box style={{ fontSize: '0.7rem', color: '#2dbd96', fontWeight: 600 }}>New</Box>
                        )}
                      </Flex>
                      <Box style={{ fontSize: '0.78rem', color: '#8a99a8', marginBottom: '8px' }}>
                        {job.position_title}
                      </Box>
                      <Flex>
                        <JobTag>{job.office?.city || 'Remote'}</JobTag>
                        <JobTag>{job.employment_type}</JobTag>
                      </Flex>
                    </Box>
                  </MockJobCard>
                </Link>
              ))}
          </Box>
        </Box>

        {/* <ScrollIndicator
          onClick={() =>
            typeof window !== 'undefined' && document.getElementById('workflow')?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <span>Scroll</span>
        </ScrollIndicator> */}
      </Flex>
    </Box>
  );
});

export default EmployerHero;
